import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { apiFetch, buildQuery } from './client'
import type {
  Alert,
  Bot,
  BotWalkforwardCandidate,
  WalkForwardLaunch,
  BotCalendar,
  BotCapitalAdjustment,
  BotCosts,
  BotCreate,
  BotMode,
  BotModeGroup,
  BotPromotion,
  BotCycle,
  BotEquityPoint,
  BotOrder,
  BotOrderRow,
  BotReport,
  BotSummary,
  ExistingBalance,
  FleetBtcBenchmark,
  FleetEquitySeries,
  FleetSnapshot,
  FleetXirr,
  Page,
  PortfolioAdvisorPreset,
  PortfolioAdvisorPresetInput,
  RiskProfile,
  RiskProfileCreate,
  Trade,
} from './types'

export function useRiskProfiles() {
  return useQuery({
    queryKey: ['risk-profiles'],
    queryFn: () => apiFetch<RiskProfile[]>('/risk-profiles'),
  })
}

export function useCreateRiskProfile() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: RiskProfileCreate) =>
      apiFetch<RiskProfile>('/risk-profiles', { method: 'POST', body: payload }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['risk-profiles'] }),
  })
}

export function useDeleteRiskProfile() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      apiFetch<{ message: string }>(`/risk-profiles/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['risk-profiles'] }),
  })
}

/** `includeDeleted` es para el módulo de historial — la key de cache queda
 *  separada de la lista de siempre (`['bots']`) para no invalidar una
 *  cuando cambia la otra por accidente; `invalidateQueries({queryKey:
 *  ['bots']})` en el resto de este archivo igual las alcanza a las dos, por
 *  el match de prefijo de default de React Query. */
export function useBots(includeDeleted = false) {
  return useQuery({
    queryKey: includeDeleted ? ['bots', 'all'] : ['bots'],
    queryFn: () =>
      apiFetch<Bot[]>(`/bots${buildQuery({ include_deleted: includeDeleted || undefined })}`),
    // Se refresca solo: el estado y el último latido los mueve `beat`, no esta pestaña.
    refetchInterval: 5000,
  })
}

export function useBot(id: number | undefined) {
  return useQuery({
    queryKey: ['bots', id],
    queryFn: () => apiFetch<Bot>(`/bots/${id}`),
    enabled: id !== undefined,
    refetchInterval: 5000,
  })
}

export function useCreateBot() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: BotCreate) => apiFetch<Bot>('/bots', { method: 'POST', body: payload }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['bots'] }),
  })
}

export function useDeleteBot() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => apiFetch<{ message: string }>(`/bots/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['bots'] }),
  })
}

function useBotAction(action: 'start' | 'pause' | 'stop') {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => apiFetch<Bot>(`/bots/${id}/${action}`, { method: 'POST' }),
    onSuccess: (bot) => {
      void queryClient.invalidateQueries({ queryKey: ['bots'] })
      void queryClient.invalidateQueries({ queryKey: ['bots', bot.id] })
    },
  })
}

export const useStartBot = () => useBotAction('start')
export const usePauseBot = () => useBotAction('pause')
export const useStopBot = () => useBotAction('stop')

/** Cierra a mercado la posición simulada de un bot en papel — lo que hace
 *  falta antes de poder promoverlo si `promotion.has_open_position` es
 *  `true`. Sólo paper: el backend rechaza esto en canary/live (usá el
 *  pánico de la cuenta ahí). Invalida `promotion` además de `bots`, a
 *  diferencia de `useBotAction`: es el único de los cuatro que le cambia
 *  el resultado a ese endpoint. */
export function useCloseBotPosition() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      apiFetch<Bot>(`/bots/${id}/close-position`, { method: 'POST' }),
    onSuccess: (bot) => {
      void queryClient.invalidateQueries({ queryKey: ['bots'] })
      void queryClient.invalidateQueries({ queryKey: ['bots', bot.id] })
      void queryClient.invalidateQueries({ queryKey: ['bots', bot.id, 'promotion'] })
    },
  })
}

export function useBotCycles(id: number | undefined) {
  return useQuery({
    queryKey: ['bots', id, 'cycles'],
    queryFn: () => apiFetch<BotCycle[]>(`/bots/${id}/cycles`),
    enabled: id !== undefined,
    refetchInterval: 10000,
  })
}

/** Por default, sólo la curva del modo ACTUAL del bot (mismo corte que
 *  `useBotSummary`, PR #411) — `fullHistory` trae todo, sin cortar por
 *  `mode_since`, para cuando el usuario quiere revisar cómo le fue en el
 *  modo anterior. */
export function useBotEquity(
  id: number | undefined,
  { fullHistory }: { fullHistory?: boolean } = {},
) {
  return useQuery({
    queryKey: ['bots', id, 'equity', fullHistory],
    queryFn: () =>
      apiFetch<BotEquityPoint[]>(
        `/bots/${id}/equity${buildQuery({ full_history: fullHistory || undefined })}`,
      ),
    enabled: id !== undefined,
    refetchInterval: 10000,
  })
}

export function useBotOrders(id: number | undefined) {
  return useQuery({
    queryKey: ['bots', id, 'orders'],
    queryFn: () => apiFetch<BotOrder[]>(`/bots/${id}/orders`),
    enabled: id !== undefined,
    refetchInterval: 10000,
  })
}

/** Tope de la API por pedido — mismo valor que `useResults` (results.ts). */
const ORDERS_PAGE_SIZE = 500

/** Cuántas filas se traen como mucho para el módulo de transacciones.
 *
 * Mismo criterio que `MAX_ROWS` en `useResults`: pensado para filtrar primero
 * (por bot, símbolo, modo, fecha) y exportar después, no para pollear una
 * lista sin fondo — si se pasa, la página lo dice en vez de recortar en
 * silencio. */
const ORDERS_MAX_ROWS = 3_000

export type BotOrderFilters = {
  bot_id?: number
  symbol?: string
  mode?: BotMode
  since?: string
  until?: string
  include_deleted?: boolean
}

export type BotOrderUniverse = {
  rows: BotOrderRow[]
  total: number
  /** `true` si hay más transacciones de las que se trajeron. */
  truncated: boolean
}

/** Transacciones de TODOS los bots del usuario — no una página, el universo
 *  completo (dentro del tope), igual criterio que `useResults`: con estos
 *  volúmenes traer todo es un pedido, y filtrar/exportar en el cliente sale
 *  gratis y sin viajes de ida y vuelta. */
export function useBotOrdersGlobal(
  filters: BotOrderFilters = {},
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: ['bots', 'orders', filters],
    enabled: options.enabled,
    queryFn: async (): Promise<BotOrderUniverse> => {
      const rows: BotOrderRow[] = []
      let total = 0

      while (rows.length < ORDERS_MAX_ROWS) {
        const query = buildQuery({
          ...filters,
          limit: ORDERS_PAGE_SIZE,
          offset: rows.length,
        })
        const page = await apiFetch<Page<BotOrderRow>>(`/bots/orders${query}`)
        total = page.total
        rows.push(...page.items)
        if (page.items.length < ORDERS_PAGE_SIZE || rows.length >= total) break
      }

      return { rows, total, truncated: rows.length < total }
    },
  })
}

/** Operaciones cerradas (viajes de ida y vuelta), no órdenes sueltas — para
 *  el subgráfico de operaciones del informe real vs. backtest. */
export function useBotTrades(id: number | undefined) {
  return useQuery({
    queryKey: ['bots', id, 'trades'],
    queryFn: () => apiFetch<Trade[]>(`/bots/${id}/trades`),
    enabled: id !== undefined,
    refetchInterval: 10000,
  })
}

/** El informe del §8: real vs. backtest.
 *
 * Sin `refetchInterval`, a diferencia del resto de la pantalla: cada llamada
 * corre un backtest de verdad en el worker, y la comparación sólo cambia
 * cuando cierra una vela nueva. Se refresca al montar y cuando el que mira lo
 * pide, no cada diez segundos.
 */
export function useBotReport(id: number | undefined) {
  return useQuery({
    queryKey: ['bots', id, 'report'],
    queryFn: () => apiFetch<BotReport>(`/bots/${id}/report`),
    enabled: id !== undefined,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  })
}

export function useBotPromotion(id: number | undefined) {
  return useQuery({
    queryKey: ['bots', id, 'promotion'],
    queryFn: () => apiFetch<BotPromotion>(`/bots/${id}/promotion`),
    enabled: id !== undefined,
  })
}

/** A pedido, nunca montado solo: es una llamada real al exchange (`fetch_
 *  balance` + a lo sumo una vela), no algo para pollear cada pocos
 *  segundos como el resto de la pantalla. `BotModeLadder` la dispara con
 *  `refetch()` justo al abrir el modal de "Pasar a…" — así el chequeo es
 *  del momento de la promoción, no de lo que quedó cacheado de antes. */
export function useBotExistingBalance(id: number | undefined) {
  return useQuery({
    queryKey: ['bots', id, 'existing-balance'],
    queryFn: () => apiFetch<ExistingBalance>(`/bots/${id}/existing-balance`),
    enabled: false,
  })
}

/** El resumen de la card. Más lento que el pool de la lista (5s, `useBots`)
 *  a propósito: reconstruye las operaciones cerradas en cada pedido, y un
 *  bot no cierra una operación cada 5 segundos. */
export function useBotSummary(id: number | undefined) {
  return useQuery({
    queryKey: ['bots', id, 'summary'],
    queryFn: () => apiFetch<BotSummary>(`/bots/${id}/summary`),
    enabled: id !== undefined,
    refetchInterval: 20_000,
  })
}

/** Todos los resúmenes en un solo pedido, para los KPIs generales y el
 *  resumen por perfil de riesgo — evita disparar un `useBotSummary` por
 *  bot desde una sección que no es la card. */
export function useBotSummaries() {
  return useQuery({
    queryKey: ['bots', 'summary'],
    queryFn: () => apiFetch<Record<number, BotSummary>>('/bots/summary'),
    refetchInterval: 20_000,
  })
}

/** Sube o baja al bot en la escalera de modos. El 403 al subir lo maneja
 *  `useWithStepUp`: el backend pide re-autenticación para subir y no para
 *  bajar, y la pantalla no tiene que saber cuál es cuál. */
/** Los puntos de las sparklines de los KPIs — uno por día, ya calculados
 *  del lado del servidor (ver app/services/fleet_snapshots.py). Sin
 *  `refetchInterval`, mismo criterio que `useBotReport`: es un dato de
 *  baja frecuencia (un punto nuevo por día), no hace falta pollearlo
 *  cada pocos segundos como al resto de la pantalla. */
export function useFleetSnapshots(days = 30) {
  return useQuery({
    queryKey: ['bots', 'fleet-snapshots', days],
    queryFn: () => apiFetch<FleetSnapshot[]>(`/bots/fleet-snapshots?days=${days}`),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  })
}

/** TIR de la flota y por bot — corre Newton-Raphson/bisección en el
 *  servidor sobre la curva de equity completa de cada bot, más caro que
 *  el resto de los KPI (ver app/api/routers/bots.py::bots_xirr). Mismo
 *  criterio sin `refetchInterval` que `useFleetSnapshots`/`useBotReport`.
 *  `modeGroup` separa production (canary+live) de test (paper) del lado
 *  del servidor — no alcanza con filtrar `per_bot` en el front, `fleet` y
 *  `per_profile` son una TIR pooled que el servidor ya calcula sobre el
 *  grupo pedido. */
export function useFleetXirr(modeGroup: BotModeGroup) {
  return useQuery({
    queryKey: ['bots', 'xirr', modeGroup],
    queryFn: () => apiFetch<FleetXirr>(`/bots/xirr${buildQuery({ mode_group: modeGroup })}`),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  })
}

/** La curva diaria de cada bot de la flota — para la curva consolidada y la
 *  matriz de correlación entre bots en vivo de /comparar (pestaña "Bots en
 *  vivo"), ver app/api/routers/bots.py::bots_fleet_equity. Mismo criterio
 *  sin `refetchInterval` que `useFleetXirr`. */
export function useFleetEquity(modeGroup: BotModeGroup, days = 90, enabled = true) {
  return useQuery({
    enabled,
    queryKey: ['bots', 'fleet-equity', modeGroup, days],
    queryFn: () =>
      apiFetch<FleetEquitySeries[]>(
        `/bots/fleet-equity${buildQuery({ mode_group: modeGroup, days })}`,
      ),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  })
}

/** Cierre diario de BTC/USDT de los últimos `days` — benchmark de "buy &
 *  hold" para el gráfico de equity consolidada de /bots, ver
 *  app/api/routers/bots.py::bots_fleet_btc_benchmark. Mismo `staleTime` que
 *  `useFleetEquity`: es la misma tarjeta, no tiene sentido que una parte se
 *  refresque y la otra no. */
export function useFleetBtcBenchmark(days = 90, enabled = true) {
  return useQuery({
    enabled,
    queryKey: ['bots', 'fleet-btc-benchmark', days],
    queryFn: () => apiFetch<FleetBtcBenchmark>(`/bots/fleet-btc-benchmark${buildQuery({ days })}`),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  })
}

/** P&L día por día de la flota, en el mismo trío que `useFleetXirr` (flota /
 *  por bot / por perfil) — ver app/api/routers/bots.py::bots_calendar.
 *  `days` es cuánto histórico trae, no cuánto se ve en pantalla: la
 *  navegación entre meses del calendario es del lado del cliente, sobre la
 *  serie ya cargada. Mismo criterio sin `refetchInterval` que
 *  `useFleetXirr`/`useFleetEquity`. */
export function useBotCalendar(modeGroup: BotModeGroup, days = 730) {
  return useQuery({
    queryKey: ['bots', 'calendar', modeGroup, days],
    queryFn: () =>
      apiFetch<BotCalendar>(`/bots/calendar${buildQuery({ mode_group: modeGroup, days })}`),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  })
}

/** Deslizamiento + funding acumulado de TODOS los bots del usuario, uno
 *  por bot — ver app/api/routers/bots.py::bot_fleet_costs. Sin
 *  `mode_group`: mismo criterio que `useBotSummaries`, quien lo consuma
 *  filtra client-side sobre el subconjunto de bots que le interesa (ver
 *  `visibleBots` en BotsPage.tsx). Mismo `staleTime` sin `refetchInterval`
 *  que `useFleetEquity`: no hace falta pollearlo tan seguido como el
 *  resto de la pantalla. */
export function useFleetCosts() {
  return useQuery({
    queryKey: ['bots', 'fleet-costs'],
    queryFn: () => apiFetch<Record<number, BotCosts>>('/bots/fleet-costs'),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  })
}

/** Operaciones cerradas de TODOS los bots del usuario, con `bot_id` propio
 *  en cada fila — ver app/api/routers/bots.py::bot_fleet_trades. `days`
 *  acota las operaciones por `exit_time`, no cuánto se ve en pantalla.
 *  Mismo criterio sin `mode_group`/`refetchInterval` que
 *  `useFleetCosts`. */
export function useFleetTrades(days = 180) {
  return useQuery({
    queryKey: ['bots', 'fleet-trades', days],
    queryFn: () => apiFetch<Trade[]>(`/bots/fleet-trades${buildQuery({ days })}`),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  })
}

export function useBotWalkforwardCandidates(id: number | undefined) {
  return useQuery({
    queryKey: ['bots', id, 'walkforward-candidates'],
    queryFn: () => apiFetch<BotWalkforwardCandidate[]>(`/bots/${id}/walkforward-candidates`),
    enabled: id !== undefined,
  })
}

/** Lanza un walk-forward con la estrategia, activo y temporalidad del bot y lo
 *  deja vinculado — la compuerta lo aceptará cuando termine de correr. */
export function useLaunchBotWalkforward() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      apiFetch<WalkForwardLaunch>(`/bots/${id}/walkforward`, { method: 'POST' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['bots'] })
      void queryClient.invalidateQueries({ queryKey: ['walkforward'] })
    },
  })
}

/** Vincula el bot a la corrida de walk-forward que lo respalda — la compuerta
 *  de promoción exige esa corrida. El backend valida que sea compatible
 *  (completada, misma estrategia, mismo activo y temporalidad). */
export function useSetBotWalkforwardSource() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, walkforwardId }: { id: number; walkforwardId: number }) =>
      apiFetch<Bot>(`/bots/${id}/walkforward-source`, {
        method: 'PATCH',
        body: { walkforward_id: walkforwardId },
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['bots'] }),
  })
}

export function useChangeBotMode() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      mode,
      overrideReason,
      confirmedCapital,
      adoptExistingBalance,
      liquidateExistingBalance,
    }: {
      id: number
      mode: BotMode
      overrideReason?: string
      //: Obligatorio al PROMOVER (el backend lo exige — auditoría
      //: 2026-09-26, FIN-02); no hace falta al bajar de modo.
      confirmedCapital?: string
      //: Sólo importa si `useBotExistingBalance` encontró un saldo real
      //: de antes del bot (hallazgo del usuario, 2026-09-29) — el backend
      //: vuelve a chequear esto server-side, así que un `undefined` acá
      //: nunca deja pasar un saldo sin resolver, sólo lo bloquea.
      adoptExistingBalance?: boolean
      //: Alternativa a adoptar: vender el saldo a mercado antes de
      //: promover (mismo hallazgo — el usuario pidió una tercera opción
      //: además de adoptar/dejar).
      liquidateExistingBalance?: boolean
    }) =>
      apiFetch<Bot>(`/bots/${id}/mode`, {
        method: 'POST',
        body: {
          mode,
          override_reason: overrideReason ?? '',
          confirmed_capital: confirmedCapital,
          adopt_existing_balance: adoptExistingBalance ?? false,
          liquidate_existing_balance: liquidateExistingBalance ?? false,
        },
      }),
    onSuccess: (bot) => {
      void queryClient.invalidateQueries({ queryKey: ['bots'] })
      void queryClient.invalidateQueries({ queryKey: ['bots', bot.id] })
      void queryClient.invalidateQueries({ queryKey: ['bots', bot.id, 'promotion'] })
    },
  })
}

/** Aprueba la versión de código vigente de la estrategia para este bot —
 *  siempre pide step-up (mismo manejo del 403 vía `useWithStepUp`), y a
 *  diferencia de `useChangeBotMode` no hay dirección "segura": aprobar
 *  código nuevo es la acción sensible en sí, no una excepción a ella. */
export function useRepinBot() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      apiFetch<Bot>(`/bots/${id}/repin`, { method: 'POST', body: { reason } }),
    onSuccess: (bot) => {
      void queryClient.invalidateQueries({ queryKey: ['bots'] })
      void queryClient.invalidateQueries({ queryKey: ['bots', bot.id] })
    },
  })
}

/** Suma (`delta` positivo) o retira (negativo) capital de un bot ya creado.
 *  El 403 al sumar lo maneja `useWithStepUp` — mismo criterio asimétrico
 *  que `useChangeBotMode`: el backend pide re-autenticación sólo para
 *  aumentar cuánto arriesga el bot, nunca para reducirlo. */
export function useAdjustBotCapital() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, delta, note }: { id: number; delta: string; note?: string }) =>
      apiFetch<BotCapitalAdjustment>(`/bots/${id}/capital`, {
        method: 'POST',
        body: { delta, note: note ?? '' },
      }),
    onSuccess: (_result, { id }) => {
      void queryClient.invalidateQueries({ queryKey: ['bots'] })
      void queryClient.invalidateQueries({ queryKey: ['bots', id] })
    },
  })
}

/** Pide al asesor de balance de flota (IA) una lectura a demanda con una
 *  pregunta preconfigurada puntual — app/services/portfolio_advisor.py,
 *  mismo trabajo que corre solo cada lunes. Devuelve la alerta
 *  (`kind="portfolio_advice"`) que el propio pedido deja creada, así que
 *  no hace falta ir a buscarla a /alertas. */
export function useRequestPortfolioAdvice() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (presetId: number) =>
      apiFetch<Alert>('/bots/portfolio-advice', {
        method: 'POST',
        body: { preset_id: presetId },
      }),
    onSuccess: (alert) => {
      queryClient.setQueryData<Alert[]>(
        ['alerts', { pending: false, kind: 'portfolio_advice' }],
        (cached) => (cached ? [alert, ...cached.filter((a) => a.id !== alert.id)] : cached),
      )
      void queryClient.invalidateQueries({ queryKey: ['alerts'] })
    },
  })
}

/** Las preguntas preconfiguradas del asesor de flota — sembradas con dos
 *  por defecto la primera vez que el usuario las lista (ver
 *  `_ensure_default_portfolio_advisor_presets` en app/api/routers/bots.py). */
export function usePortfolioAdvicePresets() {
  return useQuery({
    queryKey: ['portfolio-advice-presets'],
    queryFn: () => apiFetch<PortfolioAdvisorPreset[]>('/bots/portfolio-advice/presets'),
  })
}

export function useCreatePortfolioAdvicePreset() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: PortfolioAdvisorPresetInput) =>
      apiFetch<PortfolioAdvisorPreset>('/bots/portfolio-advice/presets', {
        method: 'POST',
        body: payload,
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['portfolio-advice-presets'] }),
  })
}

export function useUpdatePortfolioAdvicePreset() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...payload }: PortfolioAdvisorPresetInput & { id: number }) =>
      apiFetch<PortfolioAdvisorPreset>(`/bots/portfolio-advice/presets/${id}`, {
        method: 'PATCH',
        body: payload,
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['portfolio-advice-presets'] }),
  })
}

export function useDeletePortfolioAdvicePreset() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      apiFetch<{ message: string }>(`/bots/portfolio-advice/presets/${id}`, { method: 'DELETE' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['portfolio-advice-presets'] }),
  })
}
